import React, { useState, useRef, useEffect, forwardRef, useMemo } from "react";
import { Loader, Check, X, MapPin, LocateFixed } from "lucide-react";

// Add TypeScript declarations for Google Maps
declare global {
  interface Window {
    google: typeof google;
  }
}

// Google Maps namespace
declare namespace google {
  namespace maps {
    class LatLng {
      constructor(lat: number, lng: number);
      lat(): number;
      lng(): number;
    }

    interface GeocoderAddressComponent {
      long_name: string;
      short_name: string;
      types: string[];
    }

    interface GeocoderResult {
      place_id: string;
      formatted_address: string;
      geometry: {
        location: LatLng;
      };
      address_components: GeocoderAddressComponent[];
    }

    /**
     * Minimal request surface used by this app.
     * Mirrors Google Maps JS API `GeocoderRequest` options we rely on.
     */
    interface GeocoderRequest {
      placeId?: string;
      address?: string;
      location?: LatLng;
    }

    class Geocoder {
      geocode(
        request: GeocoderRequest,
        callback: (results: GeocoderResult[] | null, status: string) => void
      ): void;
    }

    namespace places {
      class AutocompleteService {
        getPlacePredictions(
          request: AutocompletionRequest,
          callback: (predictions: AutocompletePrediction[] | null, status: string) => void
        ): void;
      }

      class AutocompleteSessionToken {
        constructor();
      }

      class PlacesService {
        constructor(attributionNode: HTMLElement);
        getDetails(
          request: { placeId: string; sessionToken?: AutocompleteSessionToken },
          callback: (result: PlaceResult | null, status: string) => void
        ): void;
      }

      interface AutocompletePrediction {
        description: string;
        place_id: string;
        structured_formatting?: {
          main_text: string;
          secondary_text: string;
          main_text_matched_substrings?: Array<{
            length: number;
            offset: number;
          }>;
          secondary_text_matched_substrings?: Array<{
            length: number;
            offset: number;
          }>;
        };
      }

      interface AutocompletionRequest {
        input: string;
        types?: string[];
        sessionToken?: AutocompleteSessionToken;
      }

      interface PlaceResult {
        place_id: string;
        formatted_address?: string;
        geometry?: {
          location: LatLng;
        };
        address_components?: GeocoderAddressComponent[];
        name?: string;
      }

      const PlacesServiceStatus: {
        OK: string;
        ZERO_RESULTS: string;
        OVER_QUERY_LIMIT: string;
        REQUEST_DENIED: string;
        INVALID_REQUEST: string;
      };
    }
  }
}

export type ValidationState = 'valid' | 'invalid' | 'none';

export interface LocationData {
  formatted_address: string;
  city: string;
  state: string;
  country: string;
  /**
   * Optional neighborhood/sublocality label when a caller requests neighborhood-level granularity.
   */
  neighborhood?: string;
  name?: string;
  lat: number;
  lng: number;
  placeId: string;
}

export type LocationResultGranularity = "full" | "neighborhood";

interface LocationSearchFieldProps {
  labelText?: string;
  fieldRequired?: boolean;
  fieldPlaceholder?: string;
  validationState?: ValidationState;
  error?: string | null;
  onChange?: (location: LocationData | null) => void;
  /**
   * Called specifically when the field is cleared via the built-in clear (X) control.
   * This is useful for callers that want to treat an explicit clear differently from normal editing.
   */
  onClear?: () => void;
  value?: LocationData | null;
  className?: string;
  locationTypes?: string[]; // Array of Google Maps place types to restrict search
  /**
   * Controls what level of detail is returned & displayed.
   * - "full": returns/display `formatted_address` as provided by Google (default).
   * - "neighborhood": returns/display up to neighborhood + city + state + country (no street address).
   */
  resultGranularity?: LocationResultGranularity;
}

const LocationSearchField = forwardRef<HTMLInputElement, LocationSearchFieldProps>(
  ({ 
    labelText = "Location", 
    fieldRequired = false, 
    fieldPlaceholder = "Search for a location", 
    validationState = 'none', 
    error = null, 
    onChange, 
    onClear,
    value, 
    className = "",
    locationTypes,
    resultGranularity = "full",
  }, ref) => {
    const [isFocused, setIsFocused] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [searchResult, setSearchResult] = useState<{
      autocompleteSuggestions: Array<{
        id: string;
        description: string;
        mainText: string;
        secondaryText: string;
      }>;
      status: string;
    }>({
      autocompleteSuggestions: [],
      status: "",
    });
    const [isLoading, setIsLoading] = useState(false);
    const [apiLoaded, setApiLoaded] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [apiLoadError, setApiLoadError] = useState<string | null>(null);
    const [isGettingLocation, setIsGettingLocation] = useState(false);
    const [locationError, setLocationError] = useState<string | null>(null);

    const inputRef = useRef<HTMLInputElement>(null);
    const autocompleteService = useRef<google.maps.places.AutocompleteService | null>(null);
    const placesService = useRef<google.maps.places.PlacesService | null>(null);
    const geocoder = useRef<google.maps.Geocoder | null>(null);
    
    // Create a session token to save on API costs
    const sessionToken = useMemo(() => {
      if (window.google?.maps?.places) {
        return new window.google.maps.places.AutocompleteSessionToken();
      }
      return null;
      //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiLoaded]);

    // Load Google Maps API
    useEffect(() => {
      // Check if API is already loaded
      if (window.google?.maps?.places) {
        setApiLoaded(true);
        return;
      }
      
      const loadGoogleMapsAPI = () => {
        // Get API key from environment variables
        const apiKey = process.env.REACT_APP_GOOGLE_GEOCODE_KEY;
        
        if (!apiKey) {
          console.error('Google Maps API key is missing');
          setApiLoadError("API key not found in environment variables");
          return;
        }

        setIsLoading(true);
        
        // Check if script already exists
        if (document.getElementById('google-maps-script')) {
          return;
        }

        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
        script.defer = true;
        script.async = true;
        script.id = 'google-maps-script';
        
        script.onload = () => {
          setApiLoaded(true);
          setIsLoading(false);
        };
        
        script.onerror = () => {
          console.error('Error loading Google Maps API');
          setApiLoadError("Failed to load Google Maps API");
          setIsLoading(false);
        };
        
        document.head.appendChild(script);
      };
      
      loadGoogleMapsAPI();
    }, []);

    // Initialize Google Maps services
    useEffect(() => {
      if (!apiLoaded || !window.google?.maps?.places) {
        return;
      }
      
      try {
        autocompleteService.current = new window.google.maps.places.AutocompleteService();
        geocoder.current = new window.google.maps.Geocoder();
        
        // Create an attribution node for the PlacesService
        const dummyDiv = document.createElement('div');
        document.body.appendChild(dummyDiv);
        placesService.current = new window.google.maps.places.PlacesService(dummyDiv);
        
      } catch (error) {
        console.error('Error initializing Google Maps services:', error);
        setApiLoadError("Failed to initialize Google services");
      }
    }, [apiLoaded]);

    /**
     * Normalize a country value for display.
     * We intentionally show "USA" (not "US") to match existing app patterns.
     */
    const formatCountryForDisplay = (country: string): string => {
      if (country.trim().toUpperCase() === "US" || country.trim().toLowerCase() === "united states") {
        return "USA";
      }
      return country;
    };

    /**
     * Extract address components for neighborhood-level display.
     * This avoids street-level precision and focuses on neighborhood/city/state/country.
     */
    const extractNeighborhoodAddressParts = (
      components: google.maps.GeocoderAddressComponent[]
    ): { neighborhood: string; city: string; state: string; country: string } => {
      let neighborhood = "";
      let city = "";
      let state = "";
      let country = "";

      for (const component of components) {
        const types = component.types;

        if (!neighborhood && types.includes("neighborhood")) {
          neighborhood = component.long_name;
          continue;
        }

        // Common fallback when Google doesn't return `neighborhood`.
        if (!neighborhood && (types.includes("sublocality") || types.includes("sublocality_level_1"))) {
          neighborhood = component.long_name;
          continue;
        }

        if (!city && (types.includes("locality") || types.includes("postal_town"))) {
          city = component.long_name;
          continue;
        }

        if (!state && types.includes("administrative_area_level_1")) {
          // Use short name (e.g., "GA") to match the requested output.
          state = component.short_name;
          continue;
        }

        if (!country && types.includes("country")) {
          country = formatCountryForDisplay(component.long_name);
          continue;
        }
      }

      // If city is not found, try using administrative_area_level_2 as a last resort.
      if (!city) {
        for (const component of components) {
          if (component.types.includes("administrative_area_level_2")) {
            city = component.long_name;
            break;
          }
        }
      }

      return { neighborhood, city, state, country };
    };

    /**
     * Build the final display string for neighborhood-level mode.
     * Example: "Victoria Estates, Atlanta, GA USA"
     */
    const formatNeighborhoodDisplay = (locationData: LocationData): string => {
      const leftParts: string[] = [];
      if (locationData.neighborhood) leftParts.push(locationData.neighborhood);
      if (locationData.city && !leftParts.includes(locationData.city)) leftParts.push(locationData.city);

      const stateCountry = [locationData.state, locationData.country].filter((p) => p && p.trim().length > 0).join(" ");
      const allParts = [...leftParts, ...(stateCountry ? [stateCountry] : [])].filter((p) => p.trim().length > 0);
      return allParts.join(", ");
    };

    // Function to format displayed address based on mode
    const formatDisplayAddress = (locationData: LocationData): string => {
      if (resultGranularity === "neighborhood") {
        return formatNeighborhoodDisplay(locationData);
      }

      // If locationTypes is set, we want to display a sanitized version (city/state/country)
      if (locationTypes && locationTypes.length > 0) {
        const parts: string[] = [];
        if (locationData.city) parts.push(locationData.city);
        if (locationData.state) parts.push(locationData.state);
        if (locationData.country) parts.push(locationData.country);
        return parts.join(", ");
      }

      // Otherwise display the full address
      return locationData.formatted_address;
    };
    
    // Set input value from prop
    useEffect(() => {
      if (value) {
        // Format the display value based on whether we're showing full or partial address
        setInputValue(formatDisplayAddress(value));
      }
      //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    // Handle input change
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const query = e.target.value;
      setInputValue(query);
      
      if (!query) {
        setSearchResult({
          autocompleteSuggestions: [],
          status: '',
        });
        onChange?.(null);
        return;
      }

      // Only query when we have 2 or more characters
      if (query.length > 1 && autocompleteService.current && sessionToken) {
        setIsLoading(true);
        
        // Prepare request object with or without types restriction
        const requestObject: google.maps.places.AutocompletionRequest = {
          input: query,
          sessionToken: sessionToken,
        };
        
        // Add types if provided
        if (locationTypes && locationTypes.length > 0) {
          requestObject.types = locationTypes;
        }
        
        autocompleteService.current.getPlacePredictions(
          requestObject,
          handlePredictions
        );
      } else {
        setSearchResult({
          autocompleteSuggestions: [],
          status: '',
        });
      }
      
      function handlePredictions(predictions: google.maps.places.AutocompletePrediction[] | null, status: string) {
        setIsLoading(false);
        
        if (status === "OK" && predictions) {
          const autocompleteSuggestions = predictions.map((prediction) => {
            return {
              id: prediction.place_id,
              description: prediction.description,
              mainText: prediction.structured_formatting?.main_text || '',
              secondaryText: prediction.structured_formatting?.secondary_text || '',
            };
          });
          
          setSearchResult({
            autocompleteSuggestions: autocompleteSuggestions,
            status: 'OK',
          });
          setShowSuggestions(true);
        } else {
          setSearchResult({
            autocompleteSuggestions: [],
            status: status,
          });
        }
      }
    };

    // Extract address components (city/state/country)
    const extractAddressComponents = (
      components: google.maps.GeocoderAddressComponent[]
    ): { city: string; state: string; country: string } => {
      let city = "";
      let state = "";
      let country = "";

      components.forEach((component) => {
        const types = component.types;

        if (types.includes("locality")) {
          city = component.long_name;
        } else if (types.includes("administrative_area_level_1")) {
          state = component.long_name;
        } else if (types.includes("country")) {
          country = component.long_name;
        }

        // If city is not found, try using sublocality or administrative_area_level_2
        if (!city) {
          if (types.includes("sublocality") || types.includes("sublocality_level_1")) {
            city = component.long_name;
          } else if (types.includes("administrative_area_level_2")) {
            city = component.long_name;
          }
        }
      });

      return { city, state, country };
    };

    /**
     * Re-geocode to a less precise "neighborhood-level" location.
     * This ensures we store a placeId/lat/lng representing the neighborhood (when possible),
     * not a rooftop or street-address point.
     */
    const geocodeToNeighborhood = async (place: google.maps.GeocoderResult): Promise<{
      placeId: string;
      lat: number;
      lng: number;
      neighborhood: string;
      city: string;
      state: string;
      country: string;
      formatted_address: string;
    }> => {
      if (!geocoder.current) {
        throw new Error("Geocoder unavailable");
      }

      const { neighborhood, city, state, country } = extractNeighborhoodAddressParts(place.address_components);

      // Build a query that stays within neighborhood-level detail.
      const queryParts: string[] = [];
      if (neighborhood) queryParts.push(neighborhood);
      if (city) queryParts.push(city);
      const stateCountry = [state, country].filter((p) => p && p.trim().length > 0).join(" ");
      if (stateCountry) queryParts.push(stateCountry);

      const query = queryParts.join(", ");
      if (!query) {
        throw new Error("Unable to derive neighborhood query");
      }

      const sanitized = await new Promise<google.maps.GeocoderResult>((resolve, reject) => {
        geocoder.current?.geocode({ address: query }, (results, status) => {
          if (status === "OK" && results && results[0]) {
            resolve(results[0]);
            return;
          }
          reject(new Error(`Neighborhood geocode failed: ${status}`));
        });
      });

      return {
        placeId: sanitized.place_id,
        lat: sanitized.geometry.location.lat(),
        lng: sanitized.geometry.location.lng(),
        neighborhood,
        city,
        state,
        country,
        formatted_address: [neighborhood, city, stateCountry].filter((p) => p && p.trim().length > 0).join(", "),
      };
    };

    // Function to handle suggestion selection
    const handleSelectSuggestion = (placeId: string) => {
      if (geocoder.current && sessionToken) {
        setIsLoading(true);
        geocoder.current.geocode({ placeId }, (results: google.maps.GeocoderResult[] | null, status: string) => {
          setIsLoading(false);
          if (status === "OK" && results && results[0]) {
            const place = results[0];
            const location = place.geometry?.location;
            
            if (location) {
              const { city, state, country } = extractAddressComponents(place.address_components);

              // Neighborhood-level mode: store/display up to neighborhood.
              if (resultGranularity === "neighborhood") {
                void (async () => {
                  try {
                    const sanitized = await geocodeToNeighborhood(place);

                    const locationData: LocationData = {
                      formatted_address: sanitized.formatted_address,
                      neighborhood: sanitized.neighborhood,
                      city: sanitized.city,
                      state: sanitized.state,
                      country: sanitized.country,
                      lat: sanitized.lat,
                      lng: sanitized.lng,
                      placeId: sanitized.placeId,
                    };

                    setInputValue(formatDisplayAddress(locationData));
                    onChange?.(locationData);
                  } catch {
                    // Fallback: still strip street address from what we persist/display.
                    const parts = extractNeighborhoodAddressParts(place.address_components);
                    const fallbackStateCountry = [parts.state, parts.country].filter((p) => p && p.trim().length > 0).join(" ");
                    const fallbackFormatted = [parts.neighborhood, parts.city, fallbackStateCountry]
                      .filter((p) => p && p.trim().length > 0)
                      .join(", ");

                    const locationData: LocationData = {
                      formatted_address: fallbackFormatted || `${city}${state ? ", " + state : ""}${country ? ", " + country : ""}`,
                      neighborhood: parts.neighborhood,
                      city: parts.city || city,
                      state: parts.state || state,
                      country: parts.country || country,
                      lat: location.lat(),
                      lng: location.lng(),
                      placeId,
                    };

                    setInputValue(formatDisplayAddress(locationData));
                    onChange?.(locationData);
                  }
                })();

                setSearchResult({
                  autocompleteSuggestions: [],
                  status: "",
                });
                setShowSuggestions(false);
                return;
              }

              // If we have locationTypes set, we need to re-geocode to get the proper placeId for the city
              if (locationTypes && locationTypes.length > 0 && geocoder.current) {
                // Create location query using only city/state/country
                const locationQuery = `${city}${state ? ", " + state : ""}${country ? ", " + country : ""}`;
                
                // Re-geocode to get the placeId for the city/state/country
                geocoder.current.geocode(
                  { address: locationQuery },
                  (sanitizedResults, sanitizedStatus) => {
                    if (sanitizedStatus === "OK" && sanitizedResults && sanitizedResults.length > 0) {
                      // Use the sanitized place ID instead of the original
                      const sanitizedPlaceId = sanitizedResults[0].place_id;
                      const sanitizedLocation = sanitizedResults[0].geometry.location;
                      
                      // Get place details if needed
                      if (placesService.current && sessionToken) {
                        placesService.current.getDetails({
                          placeId: sanitizedPlaceId,
                          sessionToken: sessionToken
                        }, (placeResult, detailsStatus) => {
                          const locationData: LocationData = {
                            formatted_address: place.formatted_address,
                            city,
                            state, 
                            country,
                            name: placeResult?.name,
                            lat: sanitizedLocation.lat(),
                            lng: sanitizedLocation.lng(),
                            placeId: sanitizedPlaceId // Use sanitized placeId
                          };
                          
                          // Display only city/state/country in the input
                          setInputValue(formatDisplayAddress(locationData));
                          onChange?.(locationData);
                        });
                      } else {
                        // Fallback without place service
                        const locationData: LocationData = {
                          formatted_address: place.formatted_address,
                          city,
                          state, 
                          country,
                          lat: sanitizedLocation.lat(),
                          lng: sanitizedLocation.lng(),
                          placeId: sanitizedPlaceId // Use sanitized placeId
                        };
                        
                        // Display only city/state/country in the input
                        setInputValue(formatDisplayAddress(locationData));
                        onChange?.(locationData);
                      }
                    } else {
                      // Fall back to original geocoding if sanitized geocoding fails
                      processOriginalResult(place, location, city, state, country, placeId);
                    }
                  }
                );
              } else {
                // Process without sanitizing if not using locationTypes
                processOriginalResult(place, location, city, state, country, placeId);
              }
            }
          }
          
          setSearchResult({
            autocompleteSuggestions: [],
            status: "",
          });
          setShowSuggestions(false);
        });
      }
    };
    
    // Helper function to process original geocoding result
    const processOriginalResult = (
      place: google.maps.GeocoderResult,
      location: google.maps.LatLng,
      city: string,
      state: string,
      country: string,
      placeId: string
    ) => {
      // Get place details to check if it's a named establishment
      if (placesService.current && sessionToken) {
        placesService.current.getDetails({
          placeId: placeId,
          sessionToken: sessionToken
        }, (placeResult, detailsStatus) => {
          if (detailsStatus === "OK" && placeResult) {
            const locationData: LocationData = {
              formatted_address: place.formatted_address || '',
              city,
              state,
              country,
              name: placeResult.name, // Include the name if available
              lat: location.lat(),
              lng: location.lng(),
              placeId
            };

            // Display address appropriate for the context
            setInputValue(formatDisplayAddress(locationData));
            onChange?.(locationData);
          } else {
            // Fallback if we can't get more details
            const locationData: LocationData = {
              formatted_address: place.formatted_address || '',
              city,
              state,
              country,
              lat: location.lat(),
              lng: location.lng(),
              placeId
            };

            // Display address appropriate for the context
            setInputValue(formatDisplayAddress(locationData));
            onChange?.(locationData);
          }
        });
      } else {
        // Fallback if places service isn't available
        const locationData: LocationData = {
          formatted_address: place.formatted_address || '',
          city,
          state,
          country,
          lat: location.lat(),
          lng: location.lng(),
          placeId
        };

        // Display address appropriate for the context
        setInputValue(formatDisplayAddress(locationData));
        onChange?.(locationData);
      }
    };

    const handleClear = () => {
      setInputValue('');
      setSearchResult({
        autocompleteSuggestions: [],
        status: '',
      });
      onChange?.(null);
      onClear?.();
    };

    const handleFocus = () => {
      setIsFocused(true);
      // Show suggestions again if we have some and the input is focused
      if (searchResult.autocompleteSuggestions.length > 0) {
        setShowSuggestions(true);
      }
    };

    const handleBlur = () => {
      // Delay hiding suggestions to allow for click events
      setTimeout(() => {
        setIsFocused(false);
        setShowSuggestions(false);
      }, 200);
    };

    // Display errors based on API status
    const getErrorMessage = (status: string) => {
      if (status === '') return null;
      if (status === 'OK') return null;
      
      if (status === 'ZERO_RESULTS' || status === 'INVALID_REQUEST' || status === 'NOT_FOUND') {
        return "No places found. Try a different search term.";
      } else if (status === 'OVER_QUERY_LIMIT' || status === 'REQUEST_DENIED') {
        return "Unable to use location search at this time. Please try again later.";
      } else {
        return "Google Maps service is unavailable. Please try again later.";
      }
    };

    // Function to handle getting the user's current location
    const handleGetCurrentLocation = () => {
      if (!navigator.geolocation) {
        setLocationError("Geolocation is not supported by your browser");
        return;
      }

      setIsGettingLocation(true);
      setLocationError(null);

      navigator.geolocation.getCurrentPosition(
        position => {
          const { latitude, longitude } = position.coords;
          reverseGeocode(latitude, longitude);
        },
        error => {
          setIsGettingLocation(false);
          switch (error.code) {
            case error.PERMISSION_DENIED:
              setLocationError("Location permission denied");
              break;
            case error.POSITION_UNAVAILABLE:
              setLocationError("Location information unavailable");
              break;
            case error.TIMEOUT:
              setLocationError("Location request timed out");
              break;
            default:
              setLocationError("An unknown error occurred");
              break;
          }
        },
        { 
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    };

    // Function to reverse geocode coordinates to address
    const reverseGeocode = (latitude: number, longitude: number) => {
      if (!geocoder.current) {
        setIsGettingLocation(false);
        setLocationError("Geocoding service not available");
        return;
      }

      const latlng = new google.maps.LatLng(latitude, longitude);
      
      geocoder.current.geocode(
        { location: latlng }, 
        (results, status) => {
          setIsGettingLocation(false);
          
          if (status === "OK" && results && results.length > 0) {
            // First, extract the general location info from the result
            let resultToUse = results[0]; // Default to most specific result
            
            // If locationTypes is specified, find the first result that matches one of the types
            if (locationTypes && locationTypes.length > 0) {
              // Special case for (cities) - we need to look for locality type
              const typesToCheck = locationTypes.map(type => 
                type === '(cities)' ? 'locality' : type
              );
              
              // Find the first result that has one of the specified types
              for (const result of results) {
                // Check if any address component type matches our locationTypes
                const hasMatchingType = result.address_components.some(component => 
                  component.types.some(type => 
                    typesToCheck.includes(type)
                  )
                );
                
                if (hasMatchingType) {
                  resultToUse = result;
                  break;
                }
              }
            }
            
            const location = resultToUse.geometry?.location;
            
            if (location) {
              const { city, state, country } = extractAddressComponents(
                resultToUse.address_components
              );

              // Neighborhood-level mode: store/display up to neighborhood.
              if (resultGranularity === "neighborhood") {
                void (async () => {
                  try {
                    const sanitized = await geocodeToNeighborhood(resultToUse);
                    const locationData: LocationData = {
                      formatted_address: sanitized.formatted_address,
                      neighborhood: sanitized.neighborhood,
                      city: sanitized.city,
                      state: sanitized.state,
                      country: sanitized.country,
                      lat: sanitized.lat,
                      lng: sanitized.lng,
                      placeId: sanitized.placeId,
                    };

                    setInputValue(formatDisplayAddress(locationData));
                    onChange?.(locationData);
                  } catch {
                    const parts = extractNeighborhoodAddressParts(resultToUse.address_components);
                    const fallbackStateCountry = [parts.state, parts.country].filter((p) => p && p.trim().length > 0).join(" ");
                    const fallbackFormatted = [parts.neighborhood, parts.city, fallbackStateCountry]
                      .filter((p) => p && p.trim().length > 0)
                      .join(", ");

                    const locationData: LocationData = {
                      formatted_address: fallbackFormatted || `${city}${state ? ", " + state : ""}${country ? ", " + country : ""}`,
                      neighborhood: parts.neighborhood,
                      city: parts.city || city,
                      state: parts.state || state,
                      country: parts.country || country,
                      lat: location.lat(),
                      lng: location.lng(),
                      placeId: resultToUse.place_id,
                    };

                    setInputValue(formatDisplayAddress(locationData));
                    onChange?.(locationData);
                  }
                })();
                return;
              }
              
              // If we have locationTypes set, we need to re-geocode using just city/state/country
              // to get the proper placeId for that level, not for the specific coordinates
              if (locationTypes && locationTypes.length > 0 && geocoder.current && city) {
                // Create a location query using only city/state/country
                const locationQuery = `${city}${state ? ", " + state : ""}${country ? ", " + country : ""}`;
                
                // Re-geocode to get the proper placeId for the city level
                geocoder.current.geocode(
                  { address: locationQuery },
                  (sanitizedResults, sanitizedStatus) => {
                    if (sanitizedStatus === 'OK' && sanitizedResults && sanitizedResults.length > 0) {
                      // Use the sanitized place ID and location
                      const sanitizedPlaceId = sanitizedResults[0].place_id;
                      const sanitizedLocation = sanitizedResults[0].geometry.location;
                      
                      const locationData: LocationData = {
                        formatted_address: resultToUse.formatted_address || '',
                        city,
                        state,
                        country,
                        lat: sanitizedLocation.lat(),
                        lng: sanitizedLocation.lng(),
                        placeId: sanitizedPlaceId
                      };
                      
                      // Display address at appropriate level of detail
                      setInputValue(formatDisplayAddress(locationData));
                      onChange?.(locationData);
                    } else {
                      // Fallback to original result if re-geocoding fails
                      const locationData: LocationData = {
                        formatted_address: resultToUse.formatted_address || '',
                        city,
                        state,
                        country,
                        lat: location.lat(),
                        lng: location.lng(),
                        placeId: resultToUse.place_id
                      };
                      
                      setInputValue(formatDisplayAddress(locationData));
                      onChange?.(locationData);
                    }
                  }
                );
              } else {
                // Use original result when no locationTypes filtering is needed
                const locationData: LocationData = {
                  formatted_address: resultToUse.formatted_address || '',
                  city,
                  state,
                  country,
                  lat: location.lat(),
                  lng: location.lng(),
                  placeId: resultToUse.place_id
                };
                
                setInputValue(formatDisplayAddress(locationData));
                onChange?.(locationData);
              }
            }
          } else {
            setLocationError("Couldn't find your location");
          }
        }
      );
    };

    return (
      <div className="relative">
        <label className="relative block">
          <div className="label-container cursor-text">
            <span className="input-boundary-box relative flex items-center">
              <span className={`input-container relative flex-1 ${error ? 'error' : ''}`}>
                <input
                  ref={inputRef}
                  value={inputValue}
                  onChange={handleInputChange}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  placeholder={isFocused ? fieldPlaceholder : ''}
                  className={`
                    w-full min-h-[60px] pl-3 pr-14 pt-6 pb-1
                    eoe-input bg-gray-200 dark:bg-eoe-dark-input
                    rounded-2xl
                    text-gray-800 dark:text-eoe-dark-text
                    transition-colors
                    resize-none cursor-text
                    ${error ? 'text-warning-red dark:text-dark-warning-red' : ''}
                    ${className}
                  `}
                />
                <span 
                  className={`
                    absolute left-3
                    ${isFocused ? 'top-2 text-xs' : inputValue ? 'top-2 text-xs' : 'top-1/2 -translate-y-1/2'}
                    transition-all
                    ${error ? 'text-warning-red dark:text-dark-warning-red' : 'text-eoe-text-meta dark:text-eoe-dark-text-meta'}
                  `}
                >
                  {labelText}
                  {fieldRequired && <span className="text-warning-red dark:text-dark-warning-red ml-[0.5px]">*</span>}
                </span>
              </span>
              
              {isLoading || isGettingLocation ? (
                <span className="absolute right-3 bottom-[20%]">
                  <Loader className="h-5 w-5 animate-spin text-eoe-text-meta dark:text-eoe-dark-text-meta" />
                </span>
              ) : (
                <div className="absolute right-3 bottom-[20%] flex flex-col items-center gap-1">
                  {/* Location button */}
                  {!inputValue && (
                  <span
                    className="text-eoe-text-meta dark:text-eoe-dark-text-meta hover:text-eoe-text dark:hover:text-eoe-dark-text cursor-pointer"
                    onClick={handleGetCurrentLocation}
                    role="button"
                    aria-label="Get current location"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleGetCurrentLocation();
                      }
                    }}
                  >
                    <LocateFixed className="h-5 w-5" />
                  </span>
                  )}
                  {/* Clear button */}
                  {inputValue && (
                    <span 
                      className="text-eoe-text-meta dark:text-eoe-dark-text-meta hover:text-eoe-text dark:hover:text-eoe-dark-text cursor-pointer"
                      onClick={handleClear}
                      role="button"
                      aria-label="Clear location"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          handleClear();
                        }
                      }}
                    >
                      <X className="h-5 w-5" />
                    </span>
                  )}
                </div>
              )}
              
              {validationState !== 'none' && (
                <span className="absolute right-16 bottom-[20%]">
                  {validationState === 'valid' && !error ? (
                    <Check className="h-5 w-5 text-green-600" />
                  ) : validationState === 'invalid' ? (
                    <X className="h-5 w-5 text-warning-red dark:text-dark-warning-red" />
                  ) : null}
                </span>
              )}
            </span>
          </div>
        </label>
        
        {error && (
          <span className="mt-1 ml-3 text-xs text-warning-red dark:text-dark-warning-red">{error}</span>
        )}
        
        {apiLoadError && (
          <span className="mt-1 ml-3 text-xs text-warning-red dark:text-dark-warning-red">
            Error: {apiLoadError}
          </span>
        )}
        
        {locationError && (
          <span className="mt-1 ml-3 text-xs text-warning-red dark:text-dark-warning-red">
            {locationError}
          </span>
        )}
        
        {searchResult.status && searchResult.status !== 'OK' && (
          <span className="mt-1 ml-3 text-xs text-warning-red dark:text-dark-warning-red">
            {getErrorMessage(searchResult.status)}
          </span>
        )}
        
        {showSuggestions && searchResult.autocompleteSuggestions.length > 0 && (
          <div className="absolute z-10 w-full mt-1 bg-white dark:bg-eoe-dark-bg rounded-md shadow-lg max-h-60 overflow-auto">
            {searchResult.autocompleteSuggestions.map((suggestion) => (
              <div
                key={suggestion.id}
                className="px-4 py-2 text-sm text-eoe-text dark:text-eoe-dark-text hover:bg-eoe-bg-hover dark:hover:bg-eoe-dark-bg-hover cursor-pointer flex items-center"
                onClick={() => handleSelectSuggestion(suggestion.id)}
              >
                <MapPin className="h-4 w-4 mr-2 text-eoe-text-meta dark:text-eoe-dark-text-meta" />
                <div>
                  <div className="font-medium">{suggestion.mainText}</div>
                  {suggestion.secondaryText && (
                    <div className="text-xs text-eoe-text-meta dark:text-eoe-dark-text-meta">
                      {suggestion.secondaryText}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
);

LocationSearchField.displayName = 'LocationSearchField';

export default LocationSearchField;